import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { AppNotification } from "@/notifications/model/wire.js";
import { APP_ERROR_EVENT, qk, type AppErrorDetail } from "@/platform/client";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
} from "@/notifications/ui/queries.js";
import { NotificationWindow } from "@/notifications/ui/NotificationWindow.js";
import { everyRefresh, refreshesOf } from "@/notifications/ui/presenters.js";
import { useTz } from "@/platform/client";
import { fmtDateTime, timeAgo } from "@/platform/client";

// Two lanes (docs/notifications.md, "The two lanes, and desktop push"). The
// ephemeral lane, the application's error toasts, is local and never stored,
// shown by NotificationOverlay below. The persisted lane, what the server
// sends, is shown in NotificationBell. Dismissing always means marking read;
// only the /notifications page deletes.

interface EphemeralNotification {
  id: string;
  title: string;
  body?: string;
  href?: string | null; // in-app route to open on click (null/undefined → just dismiss)
  createdAt: number;
}

interface NotifyInput {
  title: string;
  body?: string;
  href?: string | null;
}

type DurationMode = "permanent" | "timed";
interface NotificationSettings {
  durationMode: DurationMode; // permanent = stays until dismissed
  durationSeconds: number; // auto-fade delay when timed (visual only — see firedBatchVisible)
  desktop: boolean; // also fire a browser Notification when permitted
}

type Permission = NotificationPermission | "unsupported";

interface NotificationsCtx {
  items: EphemeralNotification[];
  notify: (n: NotifyInput) => void;
  dismiss: (id: string) => void;
  snooze: (id: string) => void;
  dismissAllEphemeral: () => void;
  /** The persisted notifications that have arrived since the batch was last
   *  cleared — NOT the full unread backlog. See NotificationBell. */
  firedBatch: AppNotification[];
  /** Whether the arrivals window should currently be shown — true on every
   *  new arrival, false once faded (timed) or explicitly dismissed. On
   *  permanent mode this only ever goes false via an explicit dismiss. */
  firedBatchVisible: boolean;
  /** Marks one fired item read and removes it from the batch. */
  dismissFiredItem: (id: number) => void;
  /** Snoozes one fired item (mark read, re-surface unread in 10 min) and
   *  removes it from the batch. */
  snoozeFiredItem: (id: number) => void;
  /** Marks every currently-unread notification read (server-wide, not just
   *  the visible batch — see NotificationBell) and clears it — the explicit
   *  "dismiss all" a permanent-mode batch needs. */
  dismissFiredBatch: () => void;
  /** Resets the batch bookkeeping without marking anything read — used when
   *  the bell opens its list instead of the arrivals. */
  clearFiredBatch: () => void;
  /** Marks one notification read, full stop — what opening a row of the
   *  bell's list does (never removed from the fired batch, since it isn't in
   *  it). */
  markPersistedRead: (id: number) => void;
  settings: NotificationSettings;
  updateSettings: (patch: Partial<NotificationSettings>) => void;
  permission: Permission;
  requestPermission: () => void;
}

const DEFAULTS: NotificationSettings = {
  durationMode: "permanent",
  durationSeconds: 10,
  desktop: true,
};
const SETTINGS_KEY = "wp:notif-settings";
const SNOOZE_MS = 10 * 60 * 1000;
const supported = typeof window !== "undefined" && "Notification" in window;

const Ctx = createContext<NotificationsCtx>({
  items: [],
  notify: () => {},
  dismiss: () => {},
  snooze: () => {},
  dismissAllEphemeral: () => {},
  firedBatch: [],
  firedBatchVisible: false,
  dismissFiredItem: () => {},
  snoozeFiredItem: () => {},
  dismissFiredBatch: () => {},
  clearFiredBatch: () => {},
  markPersistedRead: () => {},
  settings: DEFAULTS,
  updateSettings: () => {},
  permission: "unsupported",
  requestPermission: () => {},
});

function loadSettings(): NotificationSettings {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") as object) };
  } catch {
    return DEFAULTS;
  }
}

// A notification's `href` is always an in-app route to navigate on click —
// every source deep-links in-app, never out. This still guards against a
// stray absolute URL landing in `href` by opening it in a new tab rather than
// handing it to the router.
export function openHref(href: string, navigate: (href: string) => void) {
  if (/^https?:\/\//i.test(href)) window.open(href, "_blank", "noopener");
  else navigate(href);
}

/** The sender of a streamed notification, read from the event's payload. */
function sourceOf(e: Event): string | null {
  try {
    const source: unknown = JSON.parse(String((e as MessageEvent).data)).source;
    return typeof source === "string" ? source : null;
  } catch {
    return null;
  }
}

// Relative while fresh; once it's old enough that "3h ago" stops being useful
// (an hour is timeAgo's own cutover from minute- to hour-granularity), switch
// to the actual clock time — notifications default to sticking around until
// dismissed, so a stale relative label would undersell how long it's been there.
export function notificationTimeLabel(createdAt: string | number, tz: string): string {
  const ms = typeof createdAt === "number" ? createdAt : Date.parse(createdAt);
  const iso = new Date(ms).toISOString();
  if (Date.now() - ms < 60 * 60_000) {
    const rel = timeAgo(iso, tz);
    return rel === "now" ? rel : `${rel} ago`;
  }
  return fmtDateTime(iso, tz);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [items, setItems] = useState<EphemeralNotification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>(loadSettings);
  const [permission, setPermission] = useState<Permission>(
    supported ? Notification.permission : "unsupported",
  );

  const settingsRef = useRef(settings);
  const navRef = useRef(navigate);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const snoozeRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  settingsRef.current = settings;
  navRef.current = navigate;

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  // ---- ephemeral lane (errors, undo hints — never persisted) ----
  const dismiss = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setItems((cur) => cur.filter((i) => i.id !== id));
  }, []);

  const notify = useCallback(
    (n: NotifyInput) => {
      const id = `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const item: EphemeralNotification = {
        id,
        title: n.title,
        body: n.body,
        href: n.href ?? null,
        createdAt: Date.now(),
      };
      setItems((cur) => [item, ...cur].slice(0, 20));

      const s = settingsRef.current;
      if (s.desktop && supported && Notification.permission === "granted") {
        try {
          const dn = new Notification(n.title, { body: n.body, tag: id });
          dn.onclick = () => {
            window.focus();
            if (item.href) openHref(item.href, navRef.current);
            dismiss(id);
          };
        } catch {
          /* some browsers throw without a service worker; ignore */
        }
      }
      if (s.durationMode === "timed") {
        const ms = Math.max(1, s.durationSeconds) * 1000;
        timersRef.current.set(
          id,
          setTimeout(() => dismiss(id), ms),
        );
      }
    },
    [dismiss],
  );

  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  useEffect(() => {
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<AppErrorDetail>).detail;
      if (detail?.title) notifyRef.current(detail);
    };
    window.addEventListener(APP_ERROR_EVENT, onError);
    return () => window.removeEventListener(APP_ERROR_EVENT, onError);
  }, []);

  const snooze = useCallback((id: string) => {
    setItems((cur) => {
      const it = cur.find((i) => i.id === id);
      if (!it) return cur;
      const t = timersRef.current.get(id);
      if (t) {
        clearTimeout(t);
        timersRef.current.delete(id);
      }
      const timer = setTimeout(() => {
        snoozeRef.current.delete(timer);
        notifyRef.current({ title: it.title, body: it.body, href: it.href });
      }, SNOOZE_MS);
      snoozeRef.current.add(timer);
      return cur.filter((i) => i.id !== id);
    });
  }, []);

  const dismissAllEphemeral = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
    setItems([]);
  }, []);

  // ---- persisted lane (server-generated — see docs/notifications.md) ----
  // Delivered live: an SSE connection below invalidates this query the
  // instant a new row lands server-side (any of the four producers), and
  // again on reconnect to resync anything created while disconnected. The
  // only thing this watches for now is "what's currently unread," which is
  // all four producers already collapse into on the server.
  const { data: persistedUnreadData } = useNotificationsList({ unreadOnly: true, limit: 20 });
  const persistedUnread = persistedUnreadData?.rows ?? [];
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const persistedSnoozeRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const seenPersistedRef = useRef<Set<number>>(new Set());
  const persistedInitRef = useRef(false);

  // Held open for the whole authenticated session (docs/notifications.md,
  // "Streaming"). A message makes the notifications stale, and whatever its
  // sender registered as refreshed by it. `open` fires on every reconnect,
  // after which everything a sender registered is read again: anything may
  // have been sent while disconnected.
  useEffect(() => {
    const refresh = (keys: readonly (readonly unknown[])[]) => {
      void qc.invalidateQueries({ queryKey: qk.notifications.all });
      for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
    };
    let connected = false;
    const es = new EventSource("/api/notifications/stream", { withCredentials: true });
    es.addEventListener("open", () => {
      refresh(connected ? everyRefresh() : []);
      connected = true;
    });
    es.addEventListener("notification", (e) => refresh(refreshesOf(sourceOf(e))));
    return () => es.close();
  }, [qc]);

  const markPersistedRead = useCallback(
    (id: number) => markRead.mutate({ id, read: true }),
    [markRead],
  );

  const snoozePersisted = useCallback(
    (id: number) => {
      markRead.mutate({ id, read: true });
      const timer = setTimeout(() => {
        persistedSnoozeRef.current.delete(timer);
        markRead.mutate({ id, read: false });
      }, SNOOZE_MS);
      persistedSnoozeRef.current.add(timer);
    },
    [markRead],
  );

  // ---- the fired batch (NotificationBell's live-arrival view) ----
  const [firedBatch, setFiredBatch] = useState<AppNotification[]>([]);
  const [firedBatchVisible, setFiredBatchVisible] = useState(false);
  const firedFadeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearFiredBatch = useCallback(() => {
    if (firedFadeTimerRef.current) {
      clearTimeout(firedFadeTimerRef.current);
      firedFadeTimerRef.current = undefined;
    }
    setFiredBatch([]);
    setFiredBatchVisible(false);
  }, []);

  // Bulk server-side UPDATE, not a loop over the (capped, batch-local)
  // firedBatch array — "mark all read" means every unread notification,
  // regardless of how many happen to be loaded here. See docs/notifications.md.
  const dismissFiredBatch = useCallback(() => {
    markAllRead.mutate();
    clearFiredBatch();
  }, [markAllRead, clearFiredBatch]);

  const dismissFiredItem = useCallback(
    (id: number) => {
      markRead.mutate({ id, read: true });
      setFiredBatch((cur) => cur.filter((n) => n.id !== id));
    },
    [markRead],
  );

  const snoozeFiredItem = useCallback(
    (id: number) => {
      snoozePersisted(id);
      setFiredBatch((cur) => cur.filter((n) => n.id !== id));
    },
    [snoozePersisted],
  );

  // An emptied batch is no longer shown.
  useEffect(() => {
    if (firedBatch.length === 0) setFiredBatchVisible(false);
  }, [firedBatch]);

  // Desktop push + the fired batch — both driven by the same "genuinely new
  // since THIS tab started watching" detection, never the pre-existing
  // unread backlog on first load (that would mean a flood of OS
  // notifications, and a fired-batch popup, on every reload) — see
  // docs/notifications.md's "Multi-device model." A row already marked read
  // by another device never appears in this query's unread result at all,
  // which is what stops it from re-firing here too.
  useEffect(() => {
    // Skip the loading pass (data still undefined, so persistedUnread is a
    // throwaway []) — otherwise it flips persistedInitRef before the real
    // first fetch lands, and that real fetch then gets misread as "not the
    // first run," flooding the fired batch (and desktop push) with the
    // entire pre-existing backlog on every cold page load.
    if (persistedUnreadData == null) return;
    const isFirstRun = !persistedInitRef.current;
    persistedInitRef.current = true;
    const arrived: AppNotification[] = [];
    for (const n of persistedUnread) {
      if (seenPersistedRef.current.has(n.id)) continue;
      seenPersistedRef.current.add(n.id);
      if (isFirstRun) continue;
      arrived.push(n);
      const s = settingsRef.current;
      if (s.desktop && supported && Notification.permission === "granted") {
        try {
          const dn = new Notification(n.title, { body: n.body ?? undefined, tag: `db-${n.id}` });
          dn.onclick = () => {
            window.focus();
            if (n.href) openHref(n.href, navRef.current);
          };
        } catch {
          /* some browsers throw without a service worker; ignore */
        }
      }
    }
    if (arrived.length === 0) return;
    setFiredBatch((cur) => [...arrived, ...cur]);
    setFiredBatchVisible(true);
    if (firedFadeTimerRef.current) clearTimeout(firedFadeTimerRef.current);
    const s = settingsRef.current;
    if (s.durationMode === "timed") {
      const ms = Math.max(1, s.durationSeconds) * 1000;
      firedFadeTimerRef.current = setTimeout(() => setFiredBatchVisible(false), ms);
    }
  }, [persistedUnread, persistedUnreadData]);

  const updateSettings = useCallback((patch: Partial<NotificationSettings>) => {
    setSettings((s) => ({ ...s, ...patch }));
  }, []);

  const requestPermission = useCallback(() => {
    if (!supported) return;
    void Notification.requestPermission().then((p) => setPermission(p));
  }, []);

  // Desktop notifications default to "on" (a preference), but the browser
  // permission itself still has to be granted separately — and since the
  // Settings toggle already reads "On" out of the box, there was never a click
  // to trigger that prompt. Ask once on load so the preference isn't a lie.
  useEffect(() => {
    if (settingsRef.current.desktop && supported && Notification.permission === "default") {
      void Notification.requestPermission().then((p) => setPermission(p));
    }
  }, []);

  // clean up every pending timer on unmount
  useEffect(() => {
    const timers = timersRef.current;
    const snoozes = snoozeRef.current;
    const persistedSnoozes = persistedSnoozeRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      snoozes.forEach((t) => clearTimeout(t));
      persistedSnoozes.forEach((t) => clearTimeout(t));
      if (firedFadeTimerRef.current) clearTimeout(firedFadeTimerRef.current);
    };
  }, []);

  const value = useMemo<NotificationsCtx>(
    () => ({
      items,
      notify,
      dismiss,
      snooze,
      dismissAllEphemeral,
      firedBatch,
      firedBatchVisible,
      dismissFiredItem,
      snoozeFiredItem,
      dismissFiredBatch,
      clearFiredBatch,
      markPersistedRead,
      settings,
      updateSettings,
      permission,
      requestPermission,
    }),
    [
      items,
      notify,
      dismiss,
      snooze,
      dismissAllEphemeral,
      firedBatch,
      firedBatchVisible,
      dismissFiredItem,
      snoozeFiredItem,
      dismissFiredBatch,
      clearFiredBatch,
      markPersistedRead,
      settings,
      updateSettings,
      permission,
      requestPermission,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useNotifications = (): NotificationsCtx => useContext(Ctx);

// The error toasts, top right: the ephemeral lane only.
export function NotificationOverlay() {
  const { items, dismiss, snooze, dismissAllEphemeral } = useNotifications();
  const navigate = useNavigate();
  const tz = useTz();
  // Re-renders so relative times keep advancing while nothing else changes.
  const [, setLocalTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setLocalTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (items.length === 0) return null;
  return (
    <NotificationWindow
      className="fixed right-3 top-[68px] z-[60] w-80 max-w-[calc(100vw-1.5rem)] sm:right-5"
      label="Dismiss all"
      items={items.map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body,
        hasHref: !!n.href,
        timeLabel: notificationTimeLabel(n.createdAt, tz),
        onOpen: () => {
          if (n.href) openHref(n.href, navigate);
          dismiss(n.id);
        },
        onDismiss: () => dismiss(n.id),
        onSnooze: () => snooze(n.id),
      }))}
      onDismissAll={dismissAllEphemeral}
    />
  );
}
